/**
 * Juntar solicitações do mesmo tipo e do mesmo cliente (23/09/2026).
 *
 * O gestor escolhe qual das selecionadas fica como PRINCIPAL (mantém número,
 * solicitante, pedido e líder); a prévia do backend diz o que vai acontecer
 * e só então ele confirma. A prévia não grava nada. As outras viram
 * "Juntada" e saem do quadro; não há desfazer.
 */
import React, { useEffect, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import { MiniSpinner } from "../../../components/LoadingMarcenaria";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { juntarSolicitacoes, previaJuncao } from "../../../services/solicitacoes";
import { formatarDataLocal, plural } from "../../../utils/dinheiro";
import { contagemItens } from "./helpers";
import { GhostBtn, InlineError, RowEnd, WarningBanner } from "./styles";

function mensagemDeErro(e, padrao) {
  return e?.payload?.detalhe || e?.message || padrao;
}

/** Frases da prévia, na ordem em que o gestor precisa ler. */
export function frasesDaPrevia(p, familia) {
  const linha = familia === "requisicao" ? ["item", "itens"] : ["peça", "peças"];
  const numeros = p.absorvidas.map((a) => a.numero).join(", ");
  const frases = [
    `${plural(p.linhas_movidas, ...linha)} ${p.linhas_movidas === 1 ? "entra" : "entram"} na ${p.principal.numero}, `
      + `que fica com ${plural(p.linhas_antes + p.linhas_movidas, ...linha)}. Nenhuma linha é somada.`,
    `${numeros} ${p.absorvidas.length === 1 ? "fica" : "ficam"} como "Juntada" e ${p.absorvidas.length === 1 ? "sai" : "saem"} do quadro. Não há como desfazer.`,
  ];
  if (p.prazo_vem_de) {
    frases.push(`O prazo passa a ser ${formatarDataLocal(p.prazo_depois)}, o mais urgente (vem da ${p.prazo_vem_de}).`);
  }
  if (p.perde_pronta) frases.push("A marcação de \"Pronta\" cai: as linhas que chegam ainda não estavam prontas.");
  if (p.perde_separada) frases.push("A marcação de \"Separada\" cai: há itens que ainda não foram separados.");
  return frases;
}

export default function JuntarModal({ solicitacoes, onClose, onJuntada }) {
  const { request } = useAuth();
  const familia = solicitacoes[0]?.familia;
  // Sugere como principal a mais antiga: é o número que circula há mais tempo.
  const [principalId, setPrincipalId] = useState(
    () => [...solicitacoes].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0]?.id,
  );
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);

  const incorporar = solicitacoes.filter((s) => s.id !== principalId).map((s) => s.id);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setErro("");
    setPrevia(null);
    previaJuncao(request, familia, principalId, incorporar)
      .then((p) => { if (vivo) setPrevia(p); })
      .catch((e) => { if (vivo) setErro(mensagemDeErro(e, "Não foi possível conferir a junção.")); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [principalId]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmar = async () => {
    try {
      const principal = await juntarSolicitacoes(request, familia, principalId, incorporar);
      await onJuntada?.(principal);
    } catch (e) {
      setErro(mensagemDeErro(e, "Não foi possível juntar as solicitações."));
      throw e;
    }
  };

  return (
    <SlideOverPanel
      open
      onClose={onClose}
      title={`Juntar ${solicitacoes.length} solicitações`}
      subtitle={`${solicitacoes[0]?.tipo} · ${solicitacoes[0]?.cliente_nome || ""}`}
      footer={(
        <RowEnd>
          <GhostBtn type="button" onClick={onClose}>Voltar</GhostBtn>
          <AsyncButton
            variant="primary"
            silentError
            successMessage="Solicitações juntadas."
            disabled={!previa || carregando}
            onClick={confirmar}
          >
            Juntar
          </AsyncButton>
        </RowEnd>
      )}
    >
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)", marginBottom: 8 }}>
          Qual fica como principal?
        </legend>
        <div style={{ display: "grid", gap: 8 }}>
          {solicitacoes.map((s) => (
            <label
              key={s.id}
              style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer", minWidth: 0 }}
            >
              <input
                type="radio"
                name="juntar-principal"
                checked={principalId === s.id}
                onChange={() => setPrincipalId(s.id)}
              />
              <strong className="mono">{s.numero}</strong>
              <span style={{ color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {contagemItens(s)} · {s.criado_por_nome || "—"}
                {s.num_pedido ? ` · pedido ${s.num_pedido}` : ""}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <div aria-live="polite" style={{ marginTop: 18, minHeight: 48, fontSize: 13.5, lineHeight: 1.5 }}>
        {carregando && <MiniSpinner size={13} label="Conferindo" />}
        {!carregando && previa && (
          <WarningBanner as="ul" style={{ margin: 0, paddingLeft: 28 }}>
            {frasesDaPrevia(previa, familia).map((f) => <li key={f}>{f}</li>)}
          </WarningBanner>
        )}
      </div>
      {erro && <InlineError>{erro}</InlineError>}
    </SlideOverPanel>
  );
}
